import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, X } from 'lucide-react'
import { fetchOnboardings } from '../../services/api'
import Loader from '../../components/Loader'
import Pagination from '../../components/Pagination'
import {
	Alert,
	Badge,
	Button,
	Card,
	EmptyState,
	PageHeader,
	Select,
	TextInput,
	formatDateTime,
	formatDocument,
	statusTone,
} from '../../components/ui'

const CLIENT_TYPES = [
	{ value: '', label: 'Todos' },
	{ value: 'PF', label: 'PF' },
	{ value: 'PJ', label: 'PJ' },
]

const emptyFilters = { name: '', document: '', accountHolderName: '', clientType: '' }

const OnboardingsList = () => {
	const navigate = useNavigate()
	const [filters, setFilters] = useState(emptyFilters)
	const [appliedFilters, setAppliedFilters] = useState(emptyFilters)
	const [page, setPage] = useState(1)
	const [data, setData] = useState(null)
	const [isLoading, setIsLoading] = useState(true)
	const [error, setError] = useState('')

	const load = useCallback(async () => {
		setIsLoading(true)
		setError('')
		try {
			const response = await fetchOnboardings({
				...appliedFilters,
				page,
				perPage: 10,
			})
			setData(response)
		} catch (err) {
			setError(err.message)
			setData(null)
		} finally {
			setIsLoading(false)
		}
	}, [appliedFilters, page])

	useEffect(() => {
		load()
	}, [load])

	const handleFilterChange = (e) => {
		const { name, value } = e.target
		setFilters((prev) => ({ ...prev, [name]: value }))
	}

	const handleSubmit = (e) => {
		e.preventDefault()
		setPage(1)
		setAppliedFilters(filters)
	}

	const handleClear = () => {
		setFilters(emptyFilters)
		setAppliedFilters(emptyFilters)
		setPage(1)
	}

	const onboardings = data?.content || []

	return (
		<div>
			<PageHeader
				title="Onboardings"
				subtitle="GET /backoffice/onboardings — cadastros em analise e finalizados"
			/>

			<Card className="mb-6">
				<form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 sm:grid-cols-5">
					<TextInput
						id="name"
						name="name"
						label="Nome"
						placeholder="Nome"
						value={filters.name}
						onChange={handleFilterChange}
					/>
					<TextInput
						id="document"
						name="document"
						label="Documento"
						placeholder="CPF ou CNPJ"
						value={filters.document}
						onChange={handleFilterChange}
					/>
					<TextInput
						id="accountHolderName"
						name="accountHolderName"
						label="Titular"
						placeholder="Nome do titular"
						value={filters.accountHolderName}
						onChange={handleFilterChange}
					/>
					<Select
						id="clientType"
						name="clientType"
						label="Tipo de cliente"
						options={CLIENT_TYPES}
						value={filters.clientType}
						onChange={handleFilterChange}
					/>
					<div className="flex items-end gap-2">
						<Button type="submit">
							<Search className="h-4 w-4" />
							Buscar
						</Button>
						<Button type="button" variant="secondary" onClick={handleClear}>
							<X className="h-4 w-4" />
							Limpar
						</Button>
					</div>
				</form>
			</Card>

			<Alert onDismiss={() => setError('')}>{error}</Alert>

			<div className="overflow-hidden rounded-lg bg-white shadow">
				{isLoading ? (
					<div className="flex h-64 items-center justify-center">
						<Loader size="lg" />
					</div>
				) : onboardings.length === 0 ? (
					<EmptyState message="Nenhum onboarding encontrado" />
				) : (
					<>
						<div className="overflow-x-auto">
							<table className="min-w-full divide-y divide-gray-200">
								<thead className="bg-gray-50">
									<tr>
										<th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
											Nome
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
											Documento
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
											Tipo
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
											Status
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
											Criado em
										</th>
										<th className="px-6 py-3" />
									</tr>
								</thead>
								<tbody className="divide-y divide-gray-200 bg-white">
									{onboardings.map((onboarding) => (
										<tr key={onboarding.id} className="hover:bg-gray-50">
											<td className="px-6 py-4 text-sm text-gray-900">
												{onboarding.name || '—'}
											</td>
											<td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
												{formatDocument(onboarding.document)}
											</td>
											<td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
												{onboarding.clientType || '—'}
											</td>
											<td className="px-6 py-4">
												<Badge tone={statusTone(onboarding.status)}>
													{onboarding.status || '—'}
												</Badge>
											</td>
											<td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
												{formatDateTime(onboarding.createdAt)}
											</td>
											<td className="whitespace-nowrap px-6 py-4 text-right">
												{(onboarding.clientType === 'PJ' || onboarding.clientType === 'PF') && (
													<Button
														variant="secondary"
														onClick={() =>
															navigate(
																`/onboardings/${onboarding.clientType.toLowerCase()}/${onboarding.id}`
															)
														}
													>
														Detalhes
													</Button>
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>

						<Pagination
							currentPage={data?.currentPage || page}
							totalPages={data?.totalPages || 1}
							onPageChange={setPage}
						/>
					</>
				)}
			</div>
		</div>
	)
}

export default OnboardingsList
